/**
 * DELIVERIES TAB
 *
 * Manages delivery work items linked to a parent job.
 * Relationship: Job → Delivery via work_item_relationships (relationship_type: 'scheduled_for' | 'child_of')
 *
 * For logistics-delivery and waste-management contexts where jobs have
 * child delivery/collection/disposal work items instead of appointments.
 *
 * @date February 2026
 */

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calendar, Clock, CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw,
  MapPin, Search, ChevronDown, ChevronUp, ArrowUpDown, Plus, Truck,
  ScrollText, ClipboardList, ExternalLink, Edit2,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { WorkItem } from '../UniversalWorkItems';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import { formatDateTimeSAST } from '@/lib/sastTime';
import DeliveryModal from '@/components/work-items/DeliveryModal';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// TYPES
// ============================================================================

interface Delivery {
  id: string;
  item_number?: string;
  item_type: string;
  item_subtype?: string;
  module_context?: string | null;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  progress_percentage?: number;
  scheduled_start?: string;
  scheduled_end?: string;
  estimated_hours?: number;
  assigned_to?: number | number[] | null;
  assignee_name?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  location_address?: string | null;
  location_coordinates?: { lat?: number; lng?: number } | string | null;
  contact_id?: number | null;
  contact_name?: string | null;
  custom_fields?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

interface GeneratedDeliveryDocument {
  id: string;
  template_key: string;
  title: string;
  document_number?: string;
  status?: string;
  document_url?: string;
  created_at?: string;
}

interface DeliveriesTabProps {
  workItemId: string;
  workItem: WorkItem;
  isActive: boolean;
  onUpdate?: () => void;
  onParentWorkItemUpdate?: (updates: Partial<WorkItem>) => void;
  userRole?: 'user' | 'supervisor' | 'admin' | 'owner' | 'root';
  activeSolutionKey?: string | null;
}

const EMPTY_DELIVERIES: Delivery[] = [];
const EMPTY_GENERATED_DOCUMENTS: GeneratedDeliveryDocument[] = [];

type DeliverySortKey = 'scheduled_start' | 'title' | 'status' | 'assignee' | 'location' | 'updated_at';
type DeliverySortDirection = 'asc' | 'desc';

type DeliverySortOption = {
  value: `${DeliverySortKey}:${DeliverySortDirection}`;
  label: string;
  column: DeliverySortKey;
  direction: DeliverySortDirection;
};

type DeliveryDocumentAction = {
  key: 'waybill' | 'manifest' | 'waste_manifest';
  label: string;
  templateKey: string;
  icon: React.ElementType;
  tone: string;
};

type DeliveryDocumentSummary = {
  count: number;
  documents: GeneratedDeliveryDocument[];
};

// ============================================================================
// STATUS CONFIG
// ============================================================================

const DELIVERY_STATUSES = [
  { value: 'planning', label: 'Planning', color: 'gray' },
  { value: 'planned', label: 'Planned', color: 'blue' },
  { value: 'scheduled', label: 'Scheduled', color: 'blue' },
  { value: 'confirmed', label: 'Confirmed', color: 'cyan' },
  { value: 'dispatched', label: 'Dispatched', color: 'violet' },
  { value: 'en_route', label: 'En Route', color: 'amber' },
  { value: 'in_transit', label: 'In Transit', color: 'amber' },
  { value: 'arrived', label: 'Arrived', color: 'indigo' },
  { value: 'collected', label: 'Collected', color: 'green' },
  { value: 'delivered', label: 'Delivered', color: 'green' },
  { value: 'in_progress', label: 'In Progress', color: 'yellow' },
  { value: 'completed', label: 'Completed', color: 'green' },
  { value: 'cancelled', label: 'Cancelled', color: 'red' },
  { value: 'failed', label: 'Failed', color: 'rose' },
  { value: 'missed', label: 'Missed', color: 'red' },
];

const STATUS_ICON_MAP: Record<string, React.ElementType> = {
  planning: Calendar,
  planned: Calendar,
  scheduled: Calendar,
  confirmed: CheckCircle,
  dispatched: Truck,
  en_route: Truck,
  in_transit: Truck,
  arrived: MapPin,
  collected: CheckCircle,
  delivered: CheckCircle,
  in_progress: Clock,
  completed: CheckCircle,
  cancelled: XCircle,
  failed: AlertCircle,
  missed: AlertCircle,
};

const DELIVERY_SORT_OPTIONS: DeliverySortOption[] = [
  { value: 'scheduled_start:desc', label: 'Newest scheduled first', column: 'scheduled_start', direction: 'desc' },
  { value: 'scheduled_start:asc', label: 'Oldest scheduled first', column: 'scheduled_start', direction: 'asc' },
  { value: 'updated_at:desc', label: 'Recently updated', column: 'updated_at', direction: 'desc' },
  { value: 'updated_at:asc', label: 'Least recently updated', column: 'updated_at', direction: 'asc' },
  { value: 'title:asc', label: 'Delivery A–Z', column: 'title', direction: 'asc' },
  { value: 'title:desc', label: 'Delivery Z–A', column: 'title', direction: 'desc' },
  { value: 'assignee:asc', label: 'Assignment A–Z', column: 'assignee', direction: 'asc' },
  { value: 'status:asc', label: 'Status A–Z', column: 'status', direction: 'asc' },
];

function normalizeStatusKey(status: unknown): string {
  return typeof status === 'string' ? status.trim().toLowerCase().replace(/-/g, '_') : '';
}

function normalizeStringValue(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function formatSubtypeLabel(value: string) {
  return value.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function isWasteSubtype(delivery: Delivery): boolean {
  const subtype = normalizeStatusKey(delivery.item_subtype);
  const moduleContext = normalizeStringValue(delivery.module_context).toLowerCase();
  return subtype.startsWith('waste_') || moduleContext.includes('waste');
}

function getDeliveryDocumentTemplatePrefix(delivery: Delivery, parentWorkItem: WorkItem): string {
  const moduleContext = normalizeStringValue(delivery.module_context || parentWorkItem.module_context).toLowerCase();
  return moduleContext.includes('waste') ? 'waste-management' : 'logistics-delivery';
}

function getDeliveryDocumentActions(delivery: Delivery, parentWorkItem: WorkItem): DeliveryDocumentAction[] {
  const templatePrefix = getDeliveryDocumentTemplatePrefix(delivery, parentWorkItem);
  const actions: DeliveryDocumentAction[] = [];

  actions.push({
    key: 'waybill',
    label: 'Waybill',
    templateKey: 'logistics-delivery-ticket:waybill',
    icon: ScrollText,
    tone: 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50',
  });

  actions.push({
    key: isWasteSubtype(delivery) ? 'waste_manifest' : 'manifest',
    label: isWasteSubtype(delivery) ? 'Waste manifest' : 'Manifest',
    templateKey: isWasteSubtype(delivery)
      ? `${templatePrefix}-ticket:waste_manifest`
      : 'logistics-delivery-ticket:manifest',
    icon: ClipboardList,
    tone: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50',
  });

  return actions;
}

function getDocumentSummaryByTemplate(documents: GeneratedDeliveryDocument[]): Record<string, GeneratedDeliveryDocument[]> {
  return documents.reduce<Record<string, GeneratedDeliveryDocument[]>>((acc, document) => {
    const key = normalizeStringValue(document.template_key);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(document);
    return acc;
  }, {});
}

function getStatusConfig(status: string) {
  const normalizedStatus = normalizeStatusKey(status);
  return DELIVERY_STATUSES.find((config) => config.value === normalizedStatus) || { label: formatSubtypeLabel(normalizedStatus || status || 'unknown'), color: 'gray' };
}

function getStatusBadgeClass(status: string, isDark: boolean): string {
  const config = getStatusConfig(status);
  const color = config.color || 'gray';
  const darkClasses: Record<string, string> = {
    blue: 'bg-blue-900/30 text-blue-300 border-blue-700',
    cyan: 'bg-cyan-900/30 text-cyan-300 border-cyan-700',
    violet: 'bg-violet-900/30 text-violet-300 border-violet-700',
    amber: 'bg-amber-900/30 text-amber-300 border-amber-700',
    indigo: 'bg-indigo-900/30 text-indigo-300 border-indigo-700',
    yellow: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
    green: 'bg-green-900/30 text-green-300 border-green-700',
    red: 'bg-red-900/30 text-red-300 border-red-700',
    rose: 'bg-rose-900/30 text-rose-300 border-rose-700',
    gray: 'bg-gray-800 text-gray-300 border-gray-700',
  };
  const lightClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return isDark ? darkClasses[color] || darkClasses.gray : lightClasses[color] || lightClasses.gray;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function DeliveriesTab({
  workItemId,
  workItem,
  isActive,
  onUpdate,
  onParentWorkItemUpdate: _onParentWorkItemUpdate,
  userRole: _userRole = 'user',
  activeSolutionKey,
}: DeliveriesTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const parentModuleContext = workItem.module_context || 'logistics-delivery';

  const [deliveries, setDeliveries] = useState<Delivery[]>(EMPTY_DELIVERIES);
  const [loading, setLoading] = useState(true);
  const [deliverySearch, setDeliverySearch] = useState('');
  const [sort, setSort] = useState<{ column: DeliverySortKey; direction: DeliverySortDirection }>({
    column: 'scheduled_start',
    direction: 'desc',
  });
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [viewingDelivery, setViewingDelivery] = useState<WorkItem | null>(null);
  const [viewingModalOpen, setViewingModalOpen] = useState(false);
  const [documentsByDelivery, setDocumentsByDelivery] = useState<Record<string, DeliveryDocumentSummary>>({});
  const [documentLoadingIds, setDocumentLoadingIds] = useState<Record<string, boolean>>({});
  const [documentActionKey, setDocumentActionKey] = useState<string | null>(null);

  const loadGeneratedDocuments = useCallback(async (deliveryItems: Delivery[]) => {
    if (deliveryItems.length === 0) {
      setDocumentsByDelivery({});
      return;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) {
      return;
    }

    const nextLoading = Object.fromEntries(deliveryItems.map((delivery) => [delivery.id, true]));
    setDocumentLoadingIds((current) => ({ ...current, ...nextLoading }));

    const entries = await Promise.all(
      deliveryItems.map(async (delivery) => {
        try {
          const response = await fetch(`/api/v1/work-items/documents/generate?work_item_id=${encodeURIComponent(delivery.id)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const payload = await response.json().catch(() => ({}));
          const documents = Array.isArray(payload.documents) ? payload.documents as GeneratedDeliveryDocument[] : EMPTY_GENERATED_DOCUMENTS;
          return [
            delivery.id,
            {
              count: typeof payload.count === 'number' ? payload.count : documents.length,
              documents,
            },
          ] as const;
        } catch (error) {
          console.error('[DeliveriesTab] Failed to load generated documents:', error);
          return [delivery.id, { count: 0, documents: EMPTY_GENERATED_DOCUMENTS }] as const;
        }
      }),
    );

    setDocumentsByDelivery(Object.fromEntries(entries));
    setDocumentLoadingIds((current) => {
      const next = { ...current };
      for (const delivery of deliveryItems) {
        delete next[delivery.id];
      }
      return next;
    });
  }, []);

  // Load deliveries
  const loadDeliveries = useCallback(async (options?: { silent?: boolean }) => {
    if (!isActive) return;

    try {
      if (!options?.silent) {
        setLoading(true);
      }

      const tokens = authService.getTokens();
      if (!tokens) {
        notify.error('Please log in to view deliveries');
        return;
      }

      const res = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          action: 'getDeliveriesForJob',
          jobId: workItemId,
        }),
      });

      const rawResponse = await res.text();
      let responseData;
      try {
        responseData = JSON.parse(rawResponse);
      } catch {
        console.error('[DeliveriesTab] Failed to parse response:', rawResponse.slice(0, 200));
        notify.error('Failed to load deliveries: Invalid response');
        return;
      }

      if (!res.ok) {
        console.error('[DeliveriesTab] API error:', responseData);
        notify.error(responseData.error || 'Failed to load deliveries');
        return;
      }

      const fetchedDeliveries = Array.isArray(responseData.data) ? responseData.data : [];
      setDeliveries(fetchedDeliveries as Delivery[]);
      void loadGeneratedDocuments(fetchedDeliveries as Delivery[]);
    } catch (error) {
      console.error('[DeliveriesTab] Load error:', error);
      notify.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [isActive, workItemId, loadGeneratedDocuments]);

  const loadDeliveriesRef = useRef(loadDeliveries);
  useEffect(() => { loadDeliveriesRef.current = loadDeliveries; }, [loadDeliveries]);

  useEffect(() => {
    if (isActive) {
      setDeliveries(EMPTY_DELIVERIES);
      setLoading(true);
      void loadDeliveriesRef.current?.();
    }
  }, [isActive, workItemId]);

  // Filter & sort
  const filteredDeliveries = useMemo(() => {
    let result = [...deliveries];
    const normalizedSearch = deliverySearch.trim().toLowerCase();

    if (normalizedSearch) {
      result = result.filter((delivery) => {
        const documentSummary = documentsByDelivery[delivery.id]?.documents || EMPTY_GENERATED_DOCUMENTS;
        const terms = [
          delivery.title,
          delivery.item_number,
          delivery.item_subtype,
          delivery.status,
          delivery.contact_name,
          delivery.location_name,
          delivery.location_address,
          delivery.assignee_name,
          ...documentSummary.flatMap((document) => [document.title, document.document_number, document.template_key]),
        ]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .join(' ')
          .toLowerCase();

        return terms.includes(normalizedSearch);
      });
    }

    result.sort((a, b) => {
      const sortValueFor = (delivery: Delivery, column: DeliverySortKey): string | number => {
        switch (column) {
          case 'scheduled_start': {
            const value = delivery.scheduled_start ? new Date(delivery.scheduled_start).getTime() : 0;
            return Number.isNaN(value) ? 0 : value;
          }
          case 'status':
            return normalizeStatusKey(delivery.status);
          case 'assignee':
            return normalizeStringValue(delivery.assignee_name).toLowerCase();
          case 'location':
            return normalizeStringValue(delivery.location_name || delivery.location_address).toLowerCase();
          case 'updated_at': {
            const value = delivery.updated_at ? new Date(delivery.updated_at).getTime() : 0;
            return Number.isNaN(value) ? 0 : value;
          }
          case 'title':
          default:
            return `${delivery.item_number || ''} ${delivery.title || ''}`.trim().toLowerCase();
        }
      };

      const leftValue = sortValueFor(a, sort.column);
      const rightValue = sortValueFor(b, sort.column);

      let comparison = 0;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        comparison = leftValue - rightValue;
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (comparison === 0) {
        const aStart = a.scheduled_start ? new Date(a.scheduled_start).getTime() : 0;
        const bStart = b.scheduled_start ? new Date(b.scheduled_start).getTime() : 0;
        comparison = aStart - bStart;
      }

      return sort.direction === 'asc' ? comparison : comparison * -1;
    });

    return result;
  }, [deliveries, deliverySearch, documentsByDelivery, sort]);

  const activeSortOption = useMemo(() => {
    return DELIVERY_SORT_OPTIONS.find((option) => option.column === sort.column && option.direction === sort.direction) || DELIVERY_SORT_OPTIONS[0];
  }, [sort.column, sort.direction]);

  const handleSortOptionChange = useCallback((value: string) => {
    const nextSort = DELIVERY_SORT_OPTIONS.find((option) => option.value === value);
    if (!nextSort) {
      return;
    }

    setSort({ column: nextSort.column, direction: nextSort.direction });
  }, []);

  // Open delivery detail modal
  const handleOpenDelivery = useCallback((delivery: Delivery) => {
    const deliveryAsWorkItem: WorkItem = {
      id: delivery.id,
      item_number: delivery.item_number || '',
      item_type: delivery.item_type || 'delivery',
      item_subtype: delivery.item_subtype || null,
      title: delivery.title || '',
      description: delivery.description || null,
      status: delivery.status || 'scheduled',
      priority: (delivery.priority as WorkItem['priority']) || 'medium',
      progress_percentage: delivery.progress_percentage ?? 0,
      assigned_to: (typeof delivery.assigned_to === 'number' || typeof delivery.assigned_to === 'string' ? delivery.assigned_to : null) as WorkItem['assigned_to'],
      assigned_to_name: delivery.assignee_name || null,
      location_id: delivery.location_id ?? null,
      location_name: delivery.location_name || null,
      location_address: delivery.location_address || null,
      location_coordinates: delivery.location_coordinates || null,
      contact_id: delivery.contact_id ?? null,
      contact_name: delivery.contact_name || null,
      custom_fields: delivery.custom_fields || {},
      module_context: delivery.module_context || parentModuleContext,
      scheduled_start: delivery.scheduled_start || null,
      scheduled_end: delivery.scheduled_end || null,
      estimated_hours: delivery.estimated_hours ?? null,
      tags: [],
      created_at: delivery.created_at || '',
      updated_at: delivery.updated_at || '',
      parent_id: workItemId,
      parent_job_number: workItem.item_number || null,
      parent_title: workItem.title || null,
    };

    setViewingDelivery(deliveryAsWorkItem);
    setViewingModalOpen(true);
  }, [workItemId, workItem, parentModuleContext]);

  const buildDocumentFieldValues = useCallback((delivery: Delivery, templateKey: string) => {
    const customFields = delivery.custom_fields || {};
    const locationLabel = delivery.location_name || delivery.location_address || workItem.location_name || workItem.location_address || '';
    const contactName = delivery.contact_name || workItem.contact_name || '';
    const vehicleRegistration = normalizeStringValue(customFields.vehicle_registration || customFields.truck_id);
    const packageCount = Number(customFields.number_of_packages ?? customFields.total_items ?? 0) || 0;
    const totalWeight = Number(customFields.total_weight_kg ?? customFields.net_weight_kg ?? customFields.estimated_weight_kg ?? 0) || 0;

    if (templateKey.endsWith(':waybill')) {
      return {
        waybill_number: normalizeStringValue(customFields.waybill_number) || delivery.item_number || delivery.id,
        shipper_name: normalizeStringValue(workItem.title || workItem.contact_name),
        shipper_address: normalizeStringValue(workItem.location_address || workItem.location_name),
        consignee_name: contactName,
        consignee_address: normalizeStringValue(delivery.location_address || workItem.location_address),
        carrier_name: normalizeStringValue(customFields.carrier_name || workItem.title),
        vehicle_registration: vehicleRegistration,
        number_of_packages: packageCount || undefined,
        total_weight_kg: totalWeight || undefined,
        dangerous_goods: isWasteSubtype(delivery),
      };
    }

    if (templateKey.endsWith(':waste_manifest')) {
      return {
        manifest_number: normalizeStringValue(customFields.manifest_number) || delivery.item_number || delivery.id,
        waste_producer: contactName,
        waste_carrier: normalizeStringValue(customFields.waste_carrier || workItem.title),
        disposal_site: normalizeStringValue(customFields.disposal_site || customFields.disposed_at || workItem.location_name),
        waste_classification: normalizeStringValue(customFields.waste_classification || customFields.waste_type),
        total_weight_kg: totalWeight || undefined,
        certification_number: normalizeStringValue(customFields.certification_number),
        regulatory_authority: normalizeStringValue(customFields.regulatory_authority),
      };
    }

    return {
      manifest_number: normalizeStringValue(customFields.manifest_number) || delivery.item_number || delivery.id,
      departure_location: normalizeStringValue(workItem.location_name || workItem.location_address),
      destination_location: locationLabel,
      total_items: packageCount || undefined,
      total_weight_kg: totalWeight || undefined,
      carrier_name: normalizeStringValue(customFields.carrier_name || workItem.title),
      vehicle_registration: vehicleRegistration,
    };
  }, [workItem]);

  const generateDeliveryDocument = useCallback(async (delivery: Delivery, action: DeliveryDocumentAction) => {
    const tokens = authService.getTokens();
    if (!tokens) {
      notify.error('Please log in to generate paperwork');
      return;
    }

    const actionKey = `${delivery.id}:${action.templateKey}`;
    setDocumentActionKey(actionKey);

    try {
      const response = await fetch('/api/v1/work-items/documents/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          template_key: action.templateKey,
          work_item_id: delivery.id,
          field_values: buildDocumentFieldValues(delivery, action.templateKey),
          generate_pdf: false,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success === false) {
        throw new Error(payload.error || `Failed to generate ${action.label.toLowerCase()}`);
      }

      notify.success(`${action.label} generated for ${delivery.title || delivery.item_number || 'delivery'}`);
      await loadGeneratedDocuments([delivery]);

      const documentUrl = payload.document?.document_url;
      if (typeof window !== 'undefined' && typeof documentUrl === 'string' && documentUrl.length > 0) {
        window.open(documentUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('[DeliveriesTab] Document generation error:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to generate document');
    } finally {
      setDocumentActionKey(null);
    }
  }, [buildDocumentFieldValues, loadGeneratedDocuments]);

  // Stats
  const stats = useMemo(() => ({
    total: deliveries.length,
    completed: deliveries.filter((delivery) => ['completed', 'delivered', 'collected'].includes(normalizeStatusKey(delivery.status))).length,
    inProgress: deliveries.filter((delivery) => ['dispatched', 'en_route', 'in_transit', 'arrived', 'in_progress'].includes(normalizeStatusKey(delivery.status))).length,
    pending: deliveries.filter((delivery) => ['planning', 'planned', 'scheduled', 'confirmed'].includes(normalizeStatusKey(delivery.status))).length,
    paperwork: Object.values(documentsByDelivery).reduce((sum, entry) => sum + entry.count, 0),
  }), [deliveries, documentsByDelivery]);

  // Toggle sort
  const handleSort = useCallback((column: DeliverySortKey) => {
    setSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  // Quick actions for delivery status
  const handleQuickAction = useCallback(async (deliveryId: string, action: string) => {
    try {
      const tokens = authService.getTokens();
      if (!tokens) return;

      const res = await fetch('/api/v1/work-items/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          workItemId: deliveryId,
          status: action,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        notify.error(data.error || `Failed to update delivery status`);
        return;
      }

      notify.success('Delivery status updated');
      void loadDeliveries({ silent: true });
      onUpdate?.();
    } catch (error) {
      console.error('[DeliveriesTab] Quick action error:', error);
      notify.error('Failed to update delivery status');
    }
  }, [loadDeliveries, onUpdate]);

  const renderDocumentsCell = useCallback((delivery: Delivery) => {
    const summary = documentsByDelivery[delivery.id] || { count: 0, documents: EMPTY_GENERATED_DOCUMENTS };
    const groupedDocuments = getDocumentSummaryByTemplate(summary.documents);
    const availableActions = getDeliveryDocumentActions(delivery, workItem);

    return (
      <div className="space-y-2">
        {documentLoadingIds[delivery.id] ? (
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Loading paperwork…</div>
        ) : summary.count > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {summary.documents.slice(0, 3).map((document) => (
              <a
                key={document.id}
                href={document.document_url || '#'}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}
              >
                <ExternalLink className="h-3 w-3" />
                {document.document_number || formatSubtypeLabel(document.template_key.split(':').at(-1) || document.template_key)}
              </a>
            ))}
            {summary.count > 3 && (
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                +{summary.count - 3} more
              </span>
            )}
          </div>
        ) : (
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>No paperwork generated yet</div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {availableActions.map((action) => {
            const ActionIcon = action.icon;
            const isGenerating = documentActionKey === `${delivery.id}:${action.templateKey}`;
            const templateDocuments = groupedDocuments[action.templateKey] || EMPTY_GENERATED_DOCUMENTS;

            return (
              <button
                key={action.templateKey}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  void generateDeliveryDocument(delivery, action);
                }}
                disabled={isGenerating}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${action.tone}`}
                title={templateDocuments.length > 0
                  ? `${action.label} (${templateDocuments.length} generated)`
                  : `Generate ${action.label.toLowerCase()}`}
              >
                {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <ActionIcon className="h-3 w-3" />}
                {action.label}
                {templateDocuments.length > 0 ? ` (${templateDocuments.length})` : ''}
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [documentActionKey, documentLoadingIds, documentsByDelivery, generateDeliveryDocument, isDark, workItem]);

  const renderDeliverySortIcon = useCallback((column: DeliverySortKey) => {
    if (sort.column !== column) {
      return <ArrowUpDown className={`h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />;
    }

    return sort.direction === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-blue-500" />
      : <ChevronDown className="h-3.5 w-3.5 text-blue-500" />;
  }, [isDark, sort.column, sort.direction]);

  if (loading && deliveries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            Deliveries
          </h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage logistics child work items, then generate waybills and manifests directly from each stop.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowDeliveryModal(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          New Delivery
        </button>
      </div>

      <DeliveryModal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        onSuccess={() => {
          setShowDeliveryModal(false);
          void loadDeliveries({ silent: true });
          onUpdate?.();
        }}
        activeSolutionKey={activeSolutionKey}
        parentJob={workItem ? {
          id: workItem.id,
          item_number: workItem.item_number || '',
          title: workItem.title || '',
          description: workItem.description || '',
          module_context: parentModuleContext,
          contact_id: workItem.contact_id,
          contact_name: workItem.contact_name,
          location_id: workItem.location_id,
          location_name: workItem.location_name,
          location_address: workItem.location_address,
          location_coordinates: typeof workItem.location_coordinates === 'string' ? workItem.location_coordinates : null,
          custom_fields: workItem.custom_fields,
        } : null}
      />

      {/* Stats bar */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
          <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{stats.total}</p>
        </div>
        <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
          <p className="text-xs font-medium text-green-600">Completed</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{stats.completed}</p>
        </div>
        <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
          <p className="text-xs font-medium text-amber-600">In Progress</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{stats.inProgress}</p>
        </div>
        <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
          <p className="text-xs font-medium text-blue-600">Pending</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{stats.pending}</p>
        </div>
        <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
          <p className="text-xs font-medium text-indigo-600">Paperwork</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{stats.paperwork}</p>
        </div>
      </div>

      <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/80'}`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Delivery grid
              </div>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Search by stop number, title, customer, location, status, or generated paperwork — then launch logistics paperwork straight from the row.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="relative block min-w-[260px]">
                <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="search"
                  value={deliverySearch}
                  onChange={(event) => setDeliverySearch(event.target.value)}
                  placeholder="Search deliveries..."
                  className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
                />
              </label>

              <label className={`relative flex min-w-[220px] items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                <ArrowUpDown className={`h-4 w-4 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <span className="sr-only">Sort deliveries</span>
                <select
                  value={activeSortOption.value}
                  onChange={(event) => handleSortOptionChange(event.target.value)}
                  className={`w-full appearance-none bg-transparent pr-6 text-sm font-medium focus:outline-none ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                  aria-label="Sort deliveries"
                >
                  {DELIVERY_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </label>

              <button
                type="button"
                onClick={() => loadDeliveries()}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${isDark ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>

              <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                <span className="font-medium">{filteredDeliveries.length}</span>
                <span>of {deliveries.length} shown</span>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className={isDark ? 'bg-gray-800/80' : 'bg-gray-50'}>
              <tr>
                {[
                  { key: 'title', label: 'Delivery' },
                  { key: 'scheduled_start', label: 'Schedule' },
                  { key: 'status', label: 'Status' },
                  { key: 'assignee', label: 'Assignment' },
                  { key: 'location', label: 'Customer & location' },
                  { key: 'updated_at', label: 'Paperwork & progress' },
                ].map((column) => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300 hover:bg-gray-700/70' : 'text-gray-600 hover:bg-gray-100'} cursor-pointer select-none transition-colors`}
                    onClick={() => handleSort(column.key as DeliverySortKey)}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{column.label}</span>
                      {renderDeliverySortIcon(column.key as DeliverySortKey)}
                    </div>
                  </th>
                ))}
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800 bg-gray-900' : 'divide-gray-100 bg-white'}`}>
              {filteredDeliveries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Truck className={`mx-auto mb-3 h-10 w-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {deliverySearch ? 'No deliveries matched your search' : 'No deliveries yet'}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {deliverySearch ? 'Try another delivery number, customer, status, or paperwork reference.' : 'Create the first delivery stop to get paperwork flowing.'}
                    </p>
                  </td>
                </tr>
              ) : filteredDeliveries.map((delivery) => {
                const normalizedStatus = normalizeStatusKey(delivery.status);
                const StatusIcon = STATUS_ICON_MAP[normalizedStatus] || Truck;
                const statusConfig = getStatusConfig(delivery.status || '');
                const customFields = delivery.custom_fields || {};
                const totalWeight = Number(customFields.total_weight_kg ?? customFields.net_weight_kg ?? customFields.estimated_weight_kg ?? 0) || 0;
                const packageCount = Number(customFields.number_of_packages ?? customFields.total_items ?? 0) || 0;
                const vehicleRegistration = normalizeStringValue(customFields.vehicle_registration || customFields.truck_id);
                const scheduleSummary = delivery.scheduled_start
                  ? `${formatDateTimeSAST(delivery.scheduled_start)}${delivery.scheduled_end ? ` → ${formatDateTimeSAST(delivery.scheduled_end)}` : ''}`
                  : 'Unscheduled';

                return (
                  <tr
                    key={delivery.id}
                    className={`${isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                    onClick={() => handleOpenDelivery(delivery)}
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono ${isDark ? 'bg-gray-800 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                            <Truck className="h-3 w-3" />
                            {delivery.item_number || delivery.id}
                          </span>
                          {delivery.item_subtype && (
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                              {formatSubtypeLabel(delivery.item_subtype)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {delivery.title || 'Untitled Delivery'}
                          </p>
                          <div className={`mt-1 flex flex-wrap items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {vehicleRegistration && <span>Vehicle {vehicleRegistration}</span>}
                            {packageCount > 0 && <span>· {packageCount} item{packageCount === 1 ? '' : 's'}</span>}
                            {totalWeight > 0 && <span>· {totalWeight.toFixed(totalWeight % 1 === 0 ? 0 : 1)} kg</span>}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="space-y-2">
                        <div className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{scheduleSummary}</div>
                        {delivery.estimated_hours != null && (
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Planned duration {delivery.estimated_hours}h
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="space-y-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold border ${getStatusBadgeClass(normalizedStatus || delivery.status || 'gray', isDark)}`}>
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig.label}
                        </span>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {['completed', 'delivered', 'collected'].includes(normalizedStatus)
                            ? 'Operationally complete'
                            : 'Awaiting next logistics action'}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="space-y-1.5">
                        <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {delivery.assignee_name || 'Unassigned'}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Driver / operator assignment
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="space-y-1.5">
                        <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {delivery.contact_name || workItem.contact_name || 'No contact'}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {delivery.location_name || delivery.location_address || workItem.location_name || workItem.location_address || 'No location set'}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      {renderDocumentsCell(delivery)}
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="flex justify-end gap-2">
                        {['planning', 'planned', 'scheduled', 'confirmed'].includes(normalizedStatus) && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleQuickAction(delivery.id, 'dispatched');
                            }}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isDark ? 'bg-violet-900/40 text-violet-300 hover:bg-violet-900/60' : 'bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
                          >
                            Dispatch
                          </button>
                        )}
                        {['dispatched', 'en_route'].includes(normalizedStatus) && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleQuickAction(delivery.id, 'arrived');
                            }}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isDark ? 'bg-amber-900/40 text-amber-300 hover:bg-amber-900/60' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                          >
                            Arrive
                          </button>
                        )}
                        {['in_transit', 'arrived', 'in_progress'].includes(normalizedStatus) && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleQuickAction(delivery.id, 'completed');
                            }}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isDark ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                          >
                            Complete
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDelivery(delivery);
                          }}
                          className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isDark ? 'bg-gray-800 text-gray-100 hover:bg-gray-700' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                          Open
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delivery detail modal */}
      {viewingDelivery && (
        <WorkItemDetailModal
          isOpen={viewingModalOpen}
          onClose={() => {
            setViewingModalOpen(false);
            setViewingDelivery(null);
            void loadDeliveries({ silent: true });
            onUpdate?.();
          }}
          workItem={viewingDelivery}
          onRefresh={() => {
            void loadDeliveries({ silent: true });
            onUpdate?.();
          }}
        />
      )}
    </div>
  );
}
